import store from '@/store/store'
import { mapGetters } from 'vuex'
import { cleanupLines } from '@/utils/helpers/CheckDistributionLines'
import { notify } from '@/utils/helpers/Notify'
import { capitalizeFirstLetter } from '@/utils/helpers/StringOperations'
import { formatDateFields } from '@/utils/helpers/FormatDate'
import { validateDataEntryModels, checkIfFormIsSaveable } from '@/utils/helpers/Validate'
import prepareFormData from '@/utils/helpers/PrepareFormData'
import { isJeBalanced, isPcBalanced, isTransactionBalanced } from '@/utils/helpers/DataEntryViews/IsTransactionBalanced'
import { payload as batchActionPayload } from '@/components/RouteComponents/Batches/actionPayload'
import { payload as bankActionPayload } from '@/components/RouteComponents/Banks/actionPayload'
import { payload as companyActionPayload } from '@/components/RouteComponents/Companies/actionPayload'
import { payload as productionActionPayload } from '@/components/RouteComponents/Productions/actionPayload'
import { UserBatchGenerator } from '@/utils/helpers/DataEntryViews/UserBatchGenerator'
import { sortObjectByProperty } from '@/utils/helpers/ObjectSorting'
import moment from 'moment'

export const transactionCreateViewMixins = {
  data () {
    return {
      lines: [],
      dialogVisible: false,
      isOpenable: false,
      isSaveable: false,
      isSaving: false,
      lineFieldsToClear: [],
      changesWereSaved: false,
      transactionIsBalanced: true,
      resetLines: false,
      altRoute: null,
      failedLines: [],
      savedTrxId: null,
      savedTrxLineIds: [],
      failNotif: null,
      savedHeader: {},
      savedLines: {},
      headerDeltas: {},
      lineDeltas: {},
      newLines: [],
      totalRequests: 0,
      completedRequests: 0,
      initialHeaderFormState: null,
      linesHaveBeenChanged: false,
      distLinesHeight: 400,
      poList: []
    }
  },
  methods: {
    /**
     * Accepts a date string and returns it formatted according to the user's locale, determined by browser language
     * @param date
     * @return {string}
     */
    formatDateToLocale (date) {
      const locale = window.navigator.userLanguage || window.navigator.language || 'en-US'
      const m = moment(date).locale(locale)
      const localeFormat = m.localeData() && typeof m.localeData().longDateFormat === 'function' && m.localeData().longDateFormat('L')
        ? m.localeData().longDateFormat('L').replace(/\//g, '-')
        : 'YYYY-MM-DD'
      return m.format(localeFormat)
    },
    /**
     * Handles save dropdown click
     * @param command
     */
    handleDropdownClick (command) {
      switch (command) {
        case '1': // Save
          this.submitForm(false, {
            status: ['NewPo', 'EditPo'].includes(this.$route.name) ? 'POSTED' : 'OPEN'
          }, false)
          break
        case '2': // Save as draft
          this.submitForm(false)
          break
        case '3': // Save & add new
          this.submitForm(true, { status: 'OPEN' })
      }
    },
    /**
     * Sets the default state, based on the Vendor's work state
     */
    setDefaultState () {
      try {
        const vendor = this.getVendorById(this.form.vendor.value)
        if (this.form.hasOwnProperty('state') && vendor && vendor.work_state) {
          this.form.state = vendor.work_state
        }
      } catch (e) {
        console.log(e)
      }
    },
    /**
     * Specifies the batch to set in the transaction header. If the BATCH_HANDLING setting is active, then it will try
     * to return the batch where the name corresponds to `${<today's date>_<current user username>}`. If such a batch is
     * not found, or if the BATCH_HANDLING setting is not active, then it will try to return the batch that was explicitly
     * specified in the query string. Otherwise, it will check whether one or more user-specific batches exist. If so,
     * it will return the first one. Otherwise, returns the default batch.
     */
    setFormBatch (batch = null) {
      const { getBatchById, getDefaultBatch, getAllBatches, getUser, getUiSettings } = this.$store.getters
      const batchSetting = getUiSettings.BATCH_HANDLING === "''" ? null : getUiSettings.BATCH_HANDLING
      if (batchSetting && batchSetting !== 'BLANK' && UserBatchGenerator.checkIfUserBatchIsCached()) {
        return UserBatchGenerator.checkIfUserBatchIsCached()
      } else if (batchSetting && batchSetting === 'BLANK') {
        return null
      }
      const userBatches = getAllBatches.filter(v => v.user === getUser.userId)
      if (batch) {
        return getBatchById(batch)
      } else if (this.$route.query && this.$route.query.batch && !isNaN(this.$route.query.batch)) {
        return getBatchById(parseInt(this.$route.query.batch))
      } else if (userBatches.length) {
        return userBatches[0]
      }
      return getDefaultBatch
    },
    /**
     * Sets header changes
     * @param deltasObject
     * @param field
     * @param origVal
     * @param newVal
     */
    setDeltas ({ deltasObject, field, origVal, newVal } = {}) {
      if (newVal && newVal.hasOwnProperty('value')) {
        if (newVal.value && newVal.value !== origVal) {
          deltasObject[field] = newVal.value
        } else {
          delete deltasObject[field]
        }
      } else if (newVal === false || (newVal && !newVal.hasOwnProperty('value'))) {
        if ((newVal || newVal === false) && newVal !== origVal) {
          deltasObject[field] = newVal
        } else {
          delete deltasObject[field]
        }
      }
    },
    /**
     * Sets line changes
     * @param deltasObject
     * @param lineNum
     * @param field
     * @param origVal
     * @param newVal
     */
    setLineDeltas ({ deltasObject, lineNum, field, origVal, newVal } = {}) {
      if (!deltasObject.hasOwnProperty(lineNum)) {
        deltasObject[lineNum] = {}
      }
      if (newVal && newVal.hasOwnProperty('value')) {
        if (newVal.value && newVal.value !== origVal) {
          deltasObject[lineNum][field] = newVal.value
        } else {
          delete deltasObject[lineNum][field]
        }
      } else if (!newVal.hasOwnProperty('value')) {
        if ((newVal || newVal === false) && newVal !== origVal) {
          deltasObject[lineNum][field] = newVal
        } else {
          delete deltasObject[lineNum][field]
        }
      }
      if (!Object.keys(deltasObject[lineNum]).length) delete deltasObject[lineNum]
    },
    /**
     * Tracks changes to the header if the header was already saved
     */
    trackHeaderChanges () {
      if (!this.savedTrxId) return
      for (const field in this.savedHeader) {
        const newVal = field === 'amount_decimal' ? parseFloat(this.form[field]).toFixed(2) : this.form[field]
        const origVal = field === 'amount_decimal' ? parseFloat(this.savedHeader[field]).toFixed(2) : this.savedHeader[field]
        const args = { deltasObject: this.headerDeltas, field, origVal, newVal }
        this.setDeltas(args)
      }
    },
    /**
     * Tracks changes to lines that have already been saved
     * @param lineNum
     */
    trackLineChanges (lineNum) {
      if (!this.savedTrxId) return
      const newLine = this.lines.find(line => line.line_number === lineNum)
      const origLine = Object.values(this.savedLines).find(line => line.line_number === lineNum)
      if (this.transactionType.name === 'je') {
        this.transactionIsBalanced = isJeBalanced(this)
      } else if (this.transactionType.name === 'pc') {
        this.transactionIsBalanced = isPcBalanced(this)
      } else {
        isTransactionBalanced(this)
      }
      if (newLine && origLine && newLine.deleteLine) {
        this.lineDeltas[lineNum] = { deleteLine: true }
        return
      }
      if (newLine && origLine) {
        for (const field in origLine) {
          const newVal = field === 'amount_decimal' ? parseFloat(newLine.form[field]).toFixed(2) : newLine.form[field]
          const origVal = field === 'amount_decimal' ? parseFloat(origLine[field]).toFixed(2) : origLine[field]
          const args = { deltasObject: this.lineDeltas, lineNum, field, origVal, newVal }
          this.setLineDeltas(args)
        }
      }
    },
    /**
     * Handles navigation when the "Back" button is hit
     */
    goBack (reset = false) {
      let { name, prettyName } = this.transactionType
      if (name === 'ap' && this.trxAlreadyExistsMsg) {
        this.trxAlreadyExistsMsg.close()
      }
      if (this.$route.name === 'NewPr') {
        name = 'pr'
      }
      if (reset) {
        this.resetForm()
        this.resetComponent()
        notify(this, 'warning', `${prettyName} canceled`)
      }
      if (this.altRoute && Object.keys(this.altRoute).length) {
        return this.$router.push(this.altRoute)
      }
      return this.$router.push({ name: `${name.toUpperCase()}s` })
    },
    /**
     * Sets a copy of the recently saved header form
     * @param apiResponse
     */
    setSavedHeader (apiResponse) {
      const boolFields = ['payment_check_pickup', 'payment_combine_checks']
      for (const field in this.form) {
        if (apiResponse.hasOwnProperty(field)) {
          if (boolFields.includes(field)) {
            this.savedHeader[field] = apiResponse[field] || false
          } else {
            this.savedHeader[field] = apiResponse[field] || ''
          }
        }
      }
    },
    /**
     * Sets a copy of a recently saved line form
     * @param apiResponse
     */
    setSavedLine (apiResponse) {
      if (apiResponse.line && apiResponse.response && apiResponse.response.id) {
        this.savedLines[apiResponse.response.id] = Object.assign({}, apiResponse.line)
      }
    },

    /**
     * Sets a copy of a recently saved bulk line form
     * @param apiResponse
     */
    setSavedbulkLine (apiResponse) {
      if (apiResponse && apiResponse.length) {
        apiResponse.forEach(line => {
          this.savedLines[line.id] = Object.assign({}, line)
        })
      }
    },
    /**
     * Check for line changes to saved lines. If changes exist, updates or deletes lines accordingly
     * If deltas indicate that line should be deleted, send DELETE request for existing line
     * Otherwise, PATCH the existing line normally
     * TODO: find a way to reduce cyclomatic complexity
     * @param requests
     * @return {*}
     */
    loadLineUpdateOrDeleteRequests (requests) {
      const { name } = this.transactionType
      if (Object.keys(this.lineDeltas).length) {
        for (const lineNum in this.lineDeltas) {
          let savedLineId
          for (const id in this.savedLines) {
            if (!(this.savedLines[id] && this.savedLines[id].line_number)) continue
            if (parseInt(this.savedLines[id].line_number) === parseInt(lineNum)) savedLineId = id
          }
          if (this.lineDeltas[lineNum].deleteLine) {
            requests.push(this.$store.dispatch('deleteLine', { type: name, id: savedLineId }))
          } else {
            requests.push(this.$store.dispatch('updateLine', { type: name, id: savedLineId, line: this.lineDeltas[lineNum] }))
          }
        }
      }
      return requests
    },
    /**
     * Check for header changes. If changes, update trx
     * @param requests
     * @return {*}
     */
    loadHeaderUpdateRequests (requests) {
      const { name } = this.transactionType
      if (Object.keys(this.headerDeltas).length) {
        requests.push(this.$store.dispatch(`update${capitalizeFirstLetter(name)}`, { id: this.savedTrxId, data: this.headerDeltas }))
      }
      return requests
    },
    /**
     * Submits changes if the transaction was already saved and a transaction id exists
     * @param addAnother
     * @param afterSubmit
     * @param close
     * @return {Promise.<TResult>}
     */
    submitChanges ({ addAnother = false, afterSubmit = null, close = false } = {}) {
      const { prettyName } = this.transactionType
      let requests = []
      requests = this.loadHeaderUpdateRequests(requests)
      requests = this.loadLineUpdateOrDeleteRequests(requests)
      if (Object.keys(this.savedLines).length) { // Get all new lines
        const savedLineNumbers = Object.values(this.savedLines).map(line => line.line_number)
        this.newLines = this.lines.filter(line => !savedLineNumbers.includes(line.line_number) && !line.saveErr)
      }
      if (this.failedLines.length) { // Add failed lines as new lines
        const failed = this.failedLines
          .map(failedLine => {
            const actual = this.lines.find(l => l.line_number === failedLine.line.line_number)
            if (actual) return actual
            return { line_number: failedLine.line.line_number, form: failedLine.line }
          })
          .filter(failedLine => !failedLine.deleteLine)
        this.newLines = [...this.newLines, ...failed]
        this.failedLines = []
      }
      this.resetSaveProgress()
      this.removeDeletedLines()
      this.unHighlightLines()
      this.isSaving = true
      this.totalRequests = requests.length
      return Promise.all(requests)
        .then(r => {
          const opts = { id: this.savedTrxId, addAnother, afterSubmit, close }
          this.initialHeaderFormState = JSON.stringify(this.form)
          if (this.newLines.length) { // Check for new lines
            const cleanLines = cleanupLines(this.newLines)
            return this.submitLinesInBatches(cleanLines, opts) // If new lines exist (which includes failed lines), submit them in batches
          } else {
            this.handlePostSubmit(opts) // Otherwise, perform post-submit tasks
            this.isSaving = false
            this.changesWereSaved = true
          }
        })
        .catch(e => {
          console.log(e)
          this.isSaving = false
          this.changesWereSaved = false
          notify(this, 'warning', `Error saving ${prettyName} entry. Please try saving again.`)
        })
    },
    /**
     * Removes red highlighting for failed lines
     */
    unHighlightLines () {
      const lines = this.$refs['distribution-lines']
      if (lines && typeof lines.unHighlightFailedLines === 'function') {
        lines.unHighlightFailedLines(this.failedLines.map((l, i) => {
          return l && l.line && l.line.line_number ? l.line.line_number : (i + 1)
        }))
      }
    },
    /**
     * Resets the progress bar
     */
    resetSaveProgress () {
      this.totalRequests = 0
      this.completedRequests = 0
    },
    /**
     * Validates and submits the header form. If the transaction was already saved and a transaction id exists,
     * submits changes instead of treating it like a new transaction by calling this.submitChanges and returning early.
     * @param addAnother    Clears the component's internal state
     * @param afterSubmit   Performs certain tasks after the transaction is saved
     * @param close         Triggers router navigation after the transaction is saved
     */
    submitOldForm (addAnother = false, afterSubmit = null, close = false) {
      if (this.savedTrxId) {
        this.submitChanges({ addAnother, afterSubmit, close })
        return
      }
      if (this.checkForUnderRelievedPoLines && typeof this.checkForUnderRelievedPoLines === 'function' && this.checkForUnderRelievedPoLines() && !Object.keys(this.onSubmitOpts).length) {
        this.onSubmitOpts = Object.assign({ addAnother, afterSubmit, close })
        this.closePoLineModalVisible = true
        return
      }
      const { name, prettyName } = this.transactionType
      const form = Object.assign({}, this.form)
      if (name === 'je' && form.debits_decimal && typeof form.debits_decimal === 'number') {
        form.debits_decimal = form.debits_decimal.toString()
      }
      if (name === 'je' && form.credits_decimal && typeof form.credits_decimal === 'number') {
        form.credits_decimal = form.credits_decimal.toString()
      }
      const validationErrors = validateDataEntryModels(form, this.fields)
      if (!validationErrors.messages) {
        if (this.form && this.form.date) formatDateFields(this, 'date')
        if (this.form && this.form.date_due) formatDateFields(this, 'date_due')
        if (this.form && this.form.effective_date) formatDateFields(this, 'effective_date')
        if (this.form && this.form.expiration_date) formatDateFields(this, 'expiration_date')
        this.isSaving = true
        this.removeDeletedLines()
        this.resetSaveProgress()
        this.totalRequests = 1 + this.lines.length
        this.$store.dispatch(`save${capitalizeFirstLetter(name)}`, prepareFormData(form))
          .then(r => {
            if (!(r && r.id)) return Promise.reject()
            if (r.id) {
              this.setSavedHeader(r)
              this.initialHeaderFormState = JSON.stringify(this.form)
              this.completedRequests++
            }
            if (this.lines.length) {
              this.savedTrxId = r.id
              const lines = cleanupLines(this.lines)
              const opts = { id: r.id, addAnother, afterSubmit, close }
              return this.submitLinesInBatches(lines, opts)
            }
            this.handlePostSubmit(r.id, addAnother, close, afterSubmit)
          })
          .catch(e => {
            console.log(e)
            this.isSaving = false
            this.changesWereSaved = false
            if (e.body && e.body.code && e.body.code === 'PNDUP') {
              this.handleDuplicatepayments(e.body.payment_confirmation_number)
            } else {
              notify(this, 'warning', `Error saving ${prettyName} entry. Please try saving again.`)
            }
          })
      } else {
        let offset = 0 // Set offset so that notifs don't overlap each other
        validationErrors.messages.map(v => { // Render validation error notifs
          notify(this, 'warning', v, offset)
          offset += 100
        })
      }
    },
    /**
     * For bulk lines
     * Validates and submits the header form. If the transaction was already saved and a transaction id exists,
     * submits changes instead of treating it like a new transaction by calling this.submitChanges and returning early.
     * @param addAnother    Clears the component's internal state
     * @param afterSubmit   Performs certain tasks after the transaction is saved
     * @param close         Triggers router navigation after the transaction is saved
     */
    submitForm (addAnother = false, afterSubmit = null, close = false) {
      if (this.savedTrxId) {
        this.submitChanges({ addAnother, afterSubmit, close })
        return
      }
      if (this.checkForUnderRelievedPoLines && typeof this.checkForUnderRelievedPoLines === 'function' && this.checkForUnderRelievedPoLines() && !Object.keys(this.onSubmitOpts).length) {
        this.onSubmitOpts = Object.assign({ addAnother, afterSubmit, close })
        this.closePoLineModalVisible = true
        return
      }
      const { name, prettyName } = this.transactionType
      const form = Object.assign({}, this.form)
      if (name === 'je' && form.debits_decimal && typeof form.debits_decimal === 'number') {
        form.debits_decimal = form.debits_decimal.toString()
      }
      if (name === 'je' && form.credits_decimal && typeof form.credits_decimal === 'number') {
        form.credits_decimal = form.credits_decimal.toString()
      }
      const validationErrors = validateDataEntryModels(form, this.fields)
      if (!validationErrors.messages) {
        if (this.form && this.form.date) formatDateFields(this, 'date')
        if (this.form && this.form.date_due) formatDateFields(this, 'date_due')
        if (this.form && this.form.effective_date) formatDateFields(this, 'effective_date')
        if (this.form && this.form.expiration_date) formatDateFields(this, 'expiration_date')
        this.isSaving = true
        this.removeDeletedLines()
        this.resetSaveProgress()
        this.totalRequests = 1
        const lines = cleanupLines(this.lines)
        const opts = { id: this.savedTrxId, addAnother, afterSubmit, close }
        let payload = prepareFormData(form)

        if (this.lines.length) {
          const batchlines = lines.map(line => {
            let form = Object.assign({ transaction: opts.id, ignore_close: false }, line.form || line)
            if (this.transactionType.name === 'ar') form = this.setMarkupAmounts(form)
            form = this.removeEmptyLineValues(form)
            return form
          })
          payload = Object.assign({ ...payload }, { lines: batchlines })
        }
        this.$store.dispatch(`save${capitalizeFirstLetter(name)}`, payload)
          .then(r => {
            if (!(r && r.id)) return Promise.reject()
            if (r.id) {
              this.setSavedHeader(r)
              this.initialHeaderFormState = JSON.stringify(this.form)
              this.completedRequests++
            }
            if (this.lines.length) {
              this.savedTrxId = r.id
              this.savedTrxLineIds = r.lines
              this.setSavedbulkLine(r.lines)
            }
            const opts = { id: this.savedTrxId, addAnother, close, afterSubmit }
            this.handlePostSubmit(opts)
          })
          .catch(e => {
            console.log(e)
            this.isSaving = false
            this.changesWereSaved = false
            if (e.body && e.body.code && e.body.code === 'PNDUP') {
              this.handleDuplicatepayments(e.body.payment_confirmation_number)
            } else {
              notify(this, 'warning', `Error saving ${prettyName} entry. Please try saving again.`)
            }
          })
      } else {
        let offset = 0 // Set offset so that notifs don't overlap each other
        validationErrors.messages.forEach(v => { // Render validation error notifs
          notify(this, 'warning', v, offset)
          offset += 100
        })
      }
    },
    /**
     * Creates list of line numbers to be deleted, and calls removeDeletedLines on DistributionLines component,
     * which removes said lines, unhighlights any highlighted lines, and re-numbers the remaining lines.
     */
    removeDeletedLines () {
      const linesComponent = this.$refs['distribution-lines']
      const deletedLineList = this.lines.filter(line => line.deleteLine).map(line => line.line_number)
      if (linesComponent && typeof linesComponent.removeDeletedLines === 'function') {
        linesComponent.removeDeletedLines(deletedLineList)
      }
    },
    /**
     * Removes empty properties from JSON request payload
     * @param lineForm
     * @return {any}
     */
    removeEmptyLineValues (lineForm) {
      const form = Object.assign({}, lineForm)
      const lineRequirements = {}
      this.lineFields.forEach(field => {
        const { required, dataType } = field
        lineRequirements[field.name] = { required: !!required, dataType }
      })
      for (const field in lineForm) {
        if (!lineRequirements[field] && form[field]) {
          continue
        } else if (!lineRequirements[field] && !form[field]) {
          delete form[field]
          continue
        }
        const { required, dataType } = lineRequirements[field]
        const value = lineForm[field]
        if (!required && dataType === 'number' && (value === '' || value === 'undefined')) {
          delete form[field]
        } else if (!required && dataType !== 'number' && !value) {
          delete form[field]
        }
      }
      return form
    },
    /**
     * For ARs only. Moves the value of form.markup_amount to form.markup_rate or form.markup_amount_decimal based on the value
     * of form.markup_type. This allows us to have only one column for the markup amount, which can represent a
     * percentage rate or a fixed amount, and send the correct properties to the API upon submission.
     * @param lineForm
     * @return {any}
     */
    setMarkupAmounts (lineForm) {
      const form = Object.assign({}, lineForm)
      if (form.markup_type === 'AMT' && form.markup_amount) {
        form.markup_amount_decimal = form.markup_amount
        delete form.markup_rate
      } else if (form.markup_type === 'PCT' && form.markup_amount) {
        form.markup_rate = form.markup_amount
        delete form.markup_amount_decimal
      }
      delete form.markup_amount
      delete form.amount_with_markups_decimal
      return Object.assign({}, form)
    },
    /**
     * Validates and submits the distribution lines in batches of n lines at a time
     * @param lines               The lines to be saved
     * @param opts                Transaction options
     * @param attempt             Current attempt number
     * @param maxToSend           The number of lines to send at once
     * @param maxAttempts         Number of attempts to make at saving lines
     */
    submitLinesInBatches (lines = [], opts, { attempt = 0, maxToSend = 10, maxAttempts = 1 } = {}) {
      this.newLines = []
      const batch = lines.splice(0, maxToSend).map(line => {
        let form = Object.assign({}, line.form || line)
        if (this.transactionType.name === 'ar') form = this.setMarkupAmounts(form)
        form = this.removeEmptyLineValues(form)
        return this.$store.dispatch('saveLine', {
          type: `${this.transactionType.name}s`,
          id: opts.id,
          line: form
        }).then(r => {
          if (r && r.response && r.response.id) this.completedRequests++
          return r
        })
      })
      return Promise.all(batch)
        .then(r => {
          if (!(r && r.length)) return Promise.reject('No response')
          r.forEach(v => {
            const { response } = v
            if (!response || (!response.id && response.hasOwnProperty('ok') && !response.ok)) {
              this.failedLines.push(v)
            } else {
              this.savedTrxLineIds.push(v.response.id)
              this.setSavedLine(v)
            }
          })
          return lines.length ? this.submitLinesInBatches(lines, opts) : this.checkLinesSubmit(opts, attempt, maxAttempts)
        })
        .catch(e => {
          console.log(e)
          this.isSaving = false
        })
    },
    /**
     * Verifies that lines have been submitted. If some lines have failed, re-calls this.submitLines,
     * incrementing the attempt number
     * @param opts
     * @param attempt
     * @param maxAttempts
     */
    checkLinesSubmit (opts, attempt, maxAttempts) {
      if (this.failedLines.length && (attempt < maxAttempts)) { // If some lines failed and it's the first attempt
        const fail = this.failedLines // Grab the lines that need to be resent
        this.failedLines = [] // Empty the failed lines array
        const attemptArgs = { attempt: attempt += 1, maxToSend: 10, maxAttempts }
        this.submitLinesInBatches(fail.map(v => v.line), opts, attemptArgs) // And try to submit the lines again
      } else if (this.failedLines.length && (attempt >= maxAttempts)) { // If some lines failed and it's not the first attempt
        this.isSaving = false
        this.changesWereSaved = false
        const lines = this.$refs['distribution-lines']
        if (lines && typeof lines.highlightFailedLines === 'function') {
          lines.highlightFailedLines(this.failedLines.map((l, i) => {
            return l && l.line && l.line.line_number ? l.line.line_number : (i + 1)
          }))
        }
        const failString = this.failedLines.map(v => v.line.line_number).join(', ')
        const h = this.$createElement
        this.failNotif = this.$notify({
          title: "Some lines didn't save",
          message: h('p', null, [
            h('div', { style: { display: 'block' } }, [
              h('span', null, `This transaction was saved as a draft because the following lines failed to save: ${failString}. Please review your input and try again.`)
            ])
          ]),
          type: 'error',
          duration: 0
        })
      } else {
        this.handlePostSubmit(opts)
      }
    },
    // /**
    //  * Handles tasks that need to be completed after the form is submitted
    //  * TODO: this needs more testing
    //  * @param id            The transaction id
    //  * @param addAnother    Whether the user wishes to create another new transaction. Clears component internal state.
    //  * @param close         Triggers router navigation after the transaction is saved
    //  * @param afterSubmit   Performs certain tasks after the transaction is saved
    //  * @return {*}
    //  */
    // async handlePostFormSubmitTasks ({ id, addAnother = false, close, afterSubmit }) {
    //   const { name, prettyName } = this.transactionType
    //   this.changesWereSaved = true
    //   const routeName = () => {
    //     let targetRouteName = name
    //     if (this.$route.name === 'NewPr') {
    //       targetRouteName = 'pr'
    //     }
    //     return close ? `${targetRouteName.toUpperCase()}s` : `Edit${capitalizeFirstLetter(targetRouteName)}`
    //   }
    //   if (afterSubmit) {
    //     try {
    //       const resp = await this.$store.dispatch(`update${capitalizeFirstLetter(name)}`, { id, data: afterSubmit })
    //       if (!resp) {
    //         throw new Error(`Error setting transaction status to ${afterSubmit.status}`)
    //       }
    //     } catch (e) {
    //       console.log(e)
    //       this.$router.push({ name: routeName(), params: { id }, query: { saved: false } })
    //     }
    //   }
    //   notify(this, 'success', `${prettyName} entry saved`)
    //   if (!addAnother) { // If not adding another trx, we're done here. If close is specified, nav back to list view, otherwise nav to trx edit view
    //     return this.$router.push(close
    //       ? { name: routeName(), query: { saved: true } }
    //       : { name: routeName(), params: { id }, query: { saved: true } }
    //     )
    //   }
    //   // If adding another, clear dist line changes, reset form (excluding batch), and reset component state
    //   if (this.$refs && this.$refs['distribution-lines']) {
    //     this.$refs['distribution-lines'].resetAllLineChanges()
    //   }
    //   this.linesHaveBeenChanged = false
    //   this.resetForm()
    //   this.resetComponent()
    //   if (close) {
    //     this.$router.push(this.altRoute)
    //   }
    // },
    /**
     * Handles post-submit procedure
     * @param id            The transaction id
     * @param addAnother    Whether the user wishes to create another new transaction. Clears component internal state.
     * @param close         Triggers router navigation after the transaction is saved
     * @param afterSubmit   Performs certain tasks after the transaction is saved
     * @return {*}
     */
    handlePostSubmit ({ id, addAnother, close, afterSubmit } = {}) {
      const { name, prettyName } = this.transactionType
      this.changesWereSaved = true
      const routeName = () => {
        let targetRouteName = name
        if (this.$route.name === 'NewPr') {
          targetRouteName = 'pr'
        }
        return close ? `${targetRouteName.toUpperCase()}s` : `Edit${capitalizeFirstLetter(targetRouteName)}`
      }
      if (afterSubmit) {
        return this.$store.dispatch(`update${capitalizeFirstLetter(name)}`, { id, data: afterSubmit })
          .then(r => { // Send PATCH with "status": "OPEN", then send user back and confirm
            this.isSaving = false
            this.resetForm()
            this.resetComponent()
            if (this.$refs && this.$refs['distribution-lines']) {
              this.$refs['distribution-lines'].resetAllLineChanges()
            }
            this.linesHaveBeenChanged = false
            if (!r) {
              this.$router.push({ routeName: routeName(), params: { id }, query: { saved: false } })
            }
            notify(this, 'success', `${prettyName} saved`)
            if (!addAnother && close) {
              return this.$router.push({ name: routeName(), query: { saved: true } })
            } else if (!addAnother && !close) {
              return this.$router.push({ name: routeName(), params: { id }, query: { saved: true } })
            }
          })
          .catch(e => {
            console.log(e)
            notify(this, 'error', `Error saving ${prettyName}`)
          })
      } else if (!afterSubmit && addAnother) {
        notify(this, 'success', `${prettyName} entry saved`)
        this.resetForm()
        return this.resetComponent()
      }
      if (close) {
        this.isSaving = false
        this.hasBeenChanged = false
        this.isSaveable = false
        notify(this, 'success', `${prettyName} entry saved`)
        this.$router.push(this.altRoute)
      } else {
        this.isSaving = false
        this.isSaveable = false
        this.$router.push({ name: `Edit${capitalizeFirstLetter(name)}`, params: { id }, query: { saved: true } })
      }
    },
    /**
     * Resets all component internal state properties except for form
     */
    resetComponent () {
      this.savedTrxId = null
      this.lines = []
      this.savedLines = Object.assign({})
      this.savedHeader = Object.assign({})
      this.savedTrxLineIds = []
      this.isSaveable = false
      this.isOpenable = false
      this.isSaving = false
      this.changesWereSaved = false
      this.dialogVisible = false
      this.altRoute = null
      this.totalRequests = 0
      this.completedRequests = 0
      this.resetLines = true
      this.initialHeaderFormState = JSON.stringify(this.form)
      this.transactionIsBalanced = (this.transactionType.name === 'je' || this.transactionType.name === 'po')
      this.$store.dispatch('changeModalVisibility', { entityType: 'customerVendorForm', visible: false, inDataEntryView: true })
      this.lineFieldsToClear = []
      this.trxAlreadyExists = false
      if (this.trxAlreadyExistsMsg) {
        this.trxAlreadyExistsMsg.close()
      }
      this.trxAlreadyExistsMsg = null
      this.vendorNumberVerifier = ''
      this.vendorNumberCheckTimeout = null
      // const routeSpecificProperties = [ TODO: this needs more testing
      //   { name: 'currentVendorSelection', defaultVal: '' },
      //   { name: 'currentCustomerSelection', defaultVal: '' },
      //   { name: 'importPoDialogVisible', defaultVal: false },
      //   { name: 'fetchingPoData', defaultVal: false },
      //   { name: 'closePoLineModalVisible', defaultVal: false },
      //   { name: 'poList', defaultVal: [] },
      //   { name: 'openPoLines', defaultVal: [] },
      //   { name: 'availablePoLines', defaultVal: [] },
      //   { name: 'selectedPoLines', defaultVal: [] },
      //   { name: 'underRelievedPoLines', defaultVal: [] },
      //   { name: 'notificationBox', defaultVal: undefined },
      //   { name: 'active', defaultVal: 0 },
      //   { name: 'selectedCustomer', defaultVal: '' }
      // ]
      // routeSpecificProperties.forEach(prop => {
      //   if (this.hasOwnProperty(prop.name)) {
      //     this[prop.name] = prop.defaultVal
      //   }
      // })
    },
    /**
     * Populates the fields under the Vendor or Batch header inputs that display information
     * such as Type, Company, Production, Bank, etc., when a new Batch or Vendor is created
     * from this view (using the + buttons in the Vendor or Batch header fields).
     * @param modelReferenceObject
     */
    populateFieldWithNewModel (modelReferenceObject) {
      const { type, getter, id } = modelReferenceObject
      const tmp = Object.assign({}, this.form)
      tmp[type].value = id
      tmp[type].label = type === 'vendor' ? this.$store.getters[getter](id).code + ' - ' + this.$store.getters[getter](id).name : this.$store.getters[getter](id).name
      this.form = Object.assign({}, tmp)
      if (type === 'vendor') {
        if (this.transactionType.name === 'ap') {
          this.currentVendorSelection = this.form.vendor.value
        }
        this.setDefaultPaymentType()
        this.setDefaultState()
      }
      if (type === 'batch') {
        this.setBatchAttributeLineValues(this.batchAttributes)
        this.setBankAttributeLineValues(this.bankAttributes)
      }
    },
    /**
     * Sets line reset status
     */
    linesHaveBeenReset () {
      this.resetLines = false
    },
    /**
     * Sets whether or not any line in the DistributionLines component has been changed in any way
     * This does not have anything to do with valid lines that have been synced to the parent component.
     * It simply tells you whether any change has been made to any line in the DistributionLines component.
     * @param linesHaveChanged    Whether any line has been changed
     */
    setLinesHaveBeenChanged (linesHaveChanged) {
      this.linesHaveBeenChanged = linesHaveChanged
    },
    /**
     * Determines whether the transaction can be saved in its current state
     * @return {boolean}
     */
    checkIfCanBeSaved () {
      const linesToDelete = this.lines.filter(line => line.deleteLine).length
      if (linesToDelete === this.lines.length) {
        return false
      }
      return !!checkIfFormIsSaveable(this.fields, this.form)
    },
    /**
     * Focuses on the first input field
     */
    focusOnFirstField () {
      const inputs = document.getElementsByTagName('input')
      if (inputs && inputs[0]) {
        inputs[0].focus()
      }
    },
    /**
     * Sets the default payment type based on the vendor selection
     */
    setDefaultPaymentType () {
      if (this.form.hasOwnProperty('payment_type')) {
        const vendor = typeof this.getVendorById === 'function' ? this.getVendorById(this.form.vendor.value) : null
        if (vendor && Object.keys(vendor).length && vendor.default_payment_type && this.form.hasOwnProperty('payment_type')) {
          this.form.payment_type = vendor.default_payment_type
        }
      }
    },
    /**
     * Sets the selected period
     */
    setSelectedPeriod () {
      if (this.form.hasOwnProperty('period') && this.currentPeriod && this.currentPeriod.id) {
        this.form.period = this.currentPeriod.id
      }
    },
    /**
     * Filters PO lines to include only those in which the 'type' is OPEN or PARTIAL
     * @param lines
     * @return {*}
     */
    filterPoLines (lines) {
      if (typeof lines[0] === 'number' || typeof lines[0] === 'string') {
        return lines
          .map(val => this.getPoLineById(val))
          .filter(val => ['OPEN', 'PARTIAL'].includes(val.type))
          .sort((a, b) => a.line_number - b.line_number)
      } else if (typeof lines[0] === 'object') {
        return sortObjectByProperty(lines, 'line_number')
      } else {
        return []
      }
    },
    /**
     * Filters PO list lines to include only those in which the amount is equal to zero or less than zero
     * @param lines
     * @return {*}
     */
    filterPoList (pos) {
      return pos.filter(po => Number(po.remaining) !== 0)
    },
    /**
     * Sets the default tax code values on distribution lines based on the vendor selected
     */
    setDefaultTaxcodeLineValues () {
      const lineLimit = this.autoEnterLineLimit || 3
      const taxCode = this.defaultTaxcode || { code: '', id: '' }
      if (this.distLines && this.distLines.length) {
        for (let lineNum = 0; lineNum < lineLimit; lineNum++) {
          const distLine = this.distLines[lineNum]
          // Only update if the amount isn't there
          if (!distLine.line.amount_decimal) distLine.modifyLine('tax_code', { label: taxCode.code, value: taxCode.id })
        }
      }
    },
    /**
     * Sets the default account code values on distribution lines based on the vendor selected
     */
    setDefaultAccountLineValues () {
      const lineLimit = this.autoEnterLineLimit || 3
      const account = this.defaultAccount || { code: '', id: '' }
      if (this.distLines && this.distLines.length) {
        for (let lineNum = 0; lineNum < lineLimit; lineNum++) {
          const distLine = this.distLines[lineNum]
          // Only update if the amount isn't there
          if (!distLine.line.amount_decimal) distLine.modifyLine('account', { label: account.code, value: account.id })
        }
      }
    },
    /**
     * Sets the default free field code values on distribution lines based on the vendor selected
     */
    setDefaultFreeFieldLineValues () {
      const lineLimit = this.autoEnterLineLimit || 3
      const freeField = this.defaultFreeField || { code: '', id: '' }
      if (this.distLines && this.distLines.length) {
        for (let lineNum = 0; lineNum < lineLimit; lineNum++) {
          const distLine = this.distLines[lineNum]
          // Only update if the amount isn't there
          if (!distLine.line.amount_decimal) distLine.modifyLine('free_field_1', { label: freeField.code, value: freeField.id })
        }
      }
    },
    setFirstLineAmount (amount) {
      if (isNaN(amount)) return null
      if (this.distLines && this.distLines.length && !this.distLines[0].line.amount_decimal) {
        this.distLines[0].modifyLine('amount_decimal', amount)
        this.distLines[0].modifyLine('remaining_decimal', amount)
      }
    },
    setBatchAttributeLineValues (batchAttributes = null) {
      const batch = batchAttributes || this.batchAttributes || null
      if (!batch || !this.$refs['distribution-lines']) return
      const attributes = ['series', 'location', 'set', 'free_field_1', 'free_field_2', 'free_field_3', 'free_field_4', 'tax_code']
      if (this.distLines && this.distLines.length) {
        this.distLines.forEach((distLine) => {
          // Only update if the amount isn't there
          if (distLine.line.amount_decimal) return null
          attributes.forEach((attribute) => {
            // if (batch.series && batch.series.id) distLine.modifyLine('series', { label: batch.series.code, value: batch.series.id })
            if (batch[attribute] && batch[attribute].id) {
              distLine.modifyLine(attribute, { label: batch[attribute].code, value: batch[attribute].id })
            } else {
              const defaultValue = this.$store.getters[`getDefault${capitalizeFirstLetter(attribute)}`]
              distLine.modifyLine(attribute, { label: defaultValue ? defaultValue.code : '', value: defaultValue ? defaultValue.id : '' })
            }
          })
        })
      }
    },
    /**
     * Line update hook. Fires whenever a line is modified.
     * @param lineNumber    The number of the line being modified
     * @param fieldName     The name of the field being modified
     * @param newValue      The new value
     * @param instance      The line component instance
     */
    lineModified ({ lineNumber, fieldName, newValue, instance = {} }) {
      if (fieldName === 'prepayment' && typeof this.setLineAcctToCustomerDepositAcct === 'function') {
        this.setLineAcctToCustomerDepositAcct(lineNumber, newValue, instance)
      }
    },
    /**
     * Customizes the order of the distribution line fields, based on what is specified in /settings, and
     * overwrites the label and/or readOnly property, if applicable.
     * @param fields
     * @return {*}
     */
    setCustomFieldOrder (fields) {
      const order = this.$store.getters.getUiSettings.LINE_FIELD_ORDER
      if (!order) {
        return fields
      }
      try {
        const parsed = JSON.parse(order)[this.transactionType.name]
        let newFields = fields.map(() => null) // create array of blank values of length of fields
        fields.forEach(field => { // if a position is specified in 'parsed' for a given field, assign the field to that position
          const position = parsed[field.name] && parsed[field.name].position ? parseInt(parsed[field.name].position) : null
          if (parsed[field.name] && position && (position <= fields.length - 1)) { // Make sure the position specified is valid
            newFields[position] = { ...field }
          }
        }) // You should now have an array that looks like [null, null, { name: 'account'... }, null, { name: 'set'... }...], etc.
        const remaining = fields.filter(field => !(newFields.map(v => v && v.name).includes(field.name))) // get left over fields that were not specified in settings
        newFields = newFields.map(field => {
          if (!field && remaining.length) { // if there isn't a field at this index
            field = Object.assign(remaining.shift()) // take the first element of the remaining unassigned fields and set it to that index
          }
          if (field && parsed[field.name] && parsed[field.name].label) { // Allow label to be overwritten
            field.label = parsed[field.name].label
          }
          if (field.hasOwnProperty('readOnly') && parsed[field.name] && parsed[field.name].hasOwnProperty('read_only')) { // Allow readOnly to be overwritten
            field.readOnly = parsed[field.name].read_only
          }
          if (parsed[field.name] && parsed[field.name].hasOwnProperty('hidden')) { // Allow hidden property to be overwritten
            field.hidden = parsed[field.name].hidden
          }
          return field
        })
        return newFields
      } catch (e) {
        if (e instanceof SyntaxError && e.message.includes('Unexpected token')) {
          console.log('Field order settings property contains invalid JSON. Error: ' + e)
        } else {
          console.log(e)
        }
        return fields
      }
    },
    /**
     * Checks whether an invoice with this vendor and number already exists, and, if so,
     * displays a notification that allows the user to discard this AP and navigate to the
     * existing one.
     */
    async checkIfTrxExists () {
      clearTimeout(this.vendorNumberCheckTimeout)
      const { vendor, number, customer } = this.form
      const data = { conditionsMet: false, action: null }
      const { name } = this.transactionType
      switch (this.transactionType.name) {
        case 'ap':
          data.conditionsMet = (vendor.value && number)
          data.action = async () => this.$store.dispatch('fetchApsWithQuery', { vendor: vendor.value, number })
          break
        case 'po':
          data.conditionsMet = !!number
          data.action = async () => this.$store.dispatch('fetchPosWithQuery', { number })
          break
        case 'ar':
          const { payload } = await import('@/components/RouteComponents/Ars/actionPayload')
          data.conditionsMet = (customer.value && number)
          data.action = async () => this.$store.dispatch('fetchWithQuery', { ...payload, query: { customer: customer.value, number } })
          break
        case 'je':
          data.conditionsMet = !!number
          data.action = async () => this.$store.dispatch('fetchJesWithQuery', { number })
      }

      if (data.conditionsMet && typeof data.action === 'function') {
        try {
          const resp = await data.action()
          if (!resp) {
            throw new Error('No response')
          }
          if (resp.hasOwnProperty('count') && resp.count) {
            const id = Object.values(resp.entities[`${name}s`])[0].id
            this.trxAlreadyExists = true
            const h = this.$createElement
            this.trxAlreadyExistsMsg = this.$message({
              message: h('p', null, [
                h('span', null, `A transaction with this ${name === 'ap' ? 'vendor and ' : ''}number already exists! `),
                h('button', { on: { click: () => this.openExistingTrx(id) } }, `Discard current & view existing ${name.toUpperCase()}`)
              ]),
              type: 'warning',
              duration: 0,
              showClose: true
            })
          } else {
            this.trxAlreadyExists = false
            if (this.trxAlreadyExistsMsg) this.trxAlreadyExistsMsg.close()
          }
        } catch (e) {
          console.log(e)
        }
      }
    },
    /**
     * Navigates to an existing AP
     * @param id
     */
    openExistingTrx (id) {
      if (this.trxAlreadyExistsMsg) {
        this.trxAlreadyExistsMsg.close()
      }
      if (id) {
        this.isSaveable = false
        return this.$router.push({ name: `Edit${capitalizeFirstLetter(this.transactionType.name)}`, params: { id } })
      }
    },
    /** To set bank set, series and location values on change of batch */
    setBankAttributeLineValues (bankAttributes = null) {
      const bank = bankAttributes || this.bankAttributes || null
      if (!bank || !this.$refs['distribution-lines']) return
      const attributes = ['series', 'location', 'set']
      if (this.distLines && this.distLines.length) {
        this.distLines.forEach((distLine) => {
          // Only update if the amount isn't there
          if (distLine.line.amount_decimal) return null
          attributes.forEach((attribute) => {
            // if (bank.series && bank.series.id) distLine.modifyLine('series', { label: bank.series.code, value: bank.series.id })
            if (bank[attribute] && bank[attribute].id) distLine.modifyLine(attribute, { label: bank[attribute].code, value: bank[attribute].id })
          })
        })
      }
    }
  },
  computed: {
    ...mapGetters({
      getVendorById: 'getVendorById',
      currentPeriod: 'getCurrentPeriod',
      getUiSettings: 'getUiSettings',
      getAddressById: 'getAddressById',
      getStateById: 'getStateById',
      getCityById: 'getCityById'
    }),
    /**
     * Returns a boolean indicating whether or not the initial data is loaded
     * @return {boolean}
     */
    dataLoaded () {
      return this.$store.getters.getInitDataLoadedStatus
    },
    /**
     * Determines whether the form has been changed in any way
     * @return {boolean}
     */
    hasBeenChanged () {
      return this.initialHeaderFormState
        ? (JSON.stringify(this.form) !== this.initialHeaderFormState || this.linesHaveBeenChanged)
        : false
    },
    /**
     * Determines whether the customer form or the vendor form should be displayed when the user hits the "+" button
     * in the header customer/vendor field.
     * @return {string|*}
     */
    customerVendorFormType () {
      const visible = this.$store.getters.getModalVisibilityState
      if (visible.customerVendorForm && typeof visible.customerVendorForm === 'object' && visible.customerVendorForm.visible) {
        return visible.customerVendorForm.type
      }
      return 'vendor'
    },
    /**
     * Returns an array of all open or partial po lines available for the selected vendor
     * @return {Array}
     */
    vendorPoLines () {
      let ret = []
      this.poList.forEach(po => {
        const lines = this.filterPoLines(po.lines)
        if (lines && lines.length) {
          ret = [...ret, ...lines]
        }
      })
      // if (!['po', 'ar', 'bp', 'pp'].includes(this.transactionType.name)) {
      //   let availIds = ret.map(v => v.id)
      //   let setIds = this.lines.filter(v => v.form && v.form.relieves).map(v => v.form.relieves)
      //   let diff = setIds.filter(id => !availIds.includes(id))
      //   return diff.map(id => this.$store.getters.getPoLineById(id))
      // }
      return ret
    },
    headerAmount () {
      return this.form.amount_decimal
    },
    distLines () {
      return this.$refs['distribution-lines'].$refs['lines-form'].$children || null
    },
    selectedVendor () {
      if (this.currentVendorSelection || (this.form.vendor && this.form.vendor.value)) {
        const vendorCode = this.currentVendorSelection || this.form.vendor.value
        return this.getVendorById(vendorCode)
      } else {
        return null
      }
    },
    // Return first found address as a string
    vendorAddressLine () {
      if (this.selectedVendor) {
        const addressLists = ['address_selected', 'address_contact', 'address_billing', 'address_mailing']
        for (let i = 0; i < addressLists.length; i++) {
          const v = this.selectedVendor[addressLists[i]] || undefined
          let ven

          if (!v) {
            return undefined
          } else if (!isNaN(v)) {
            ven = this.getAddressById(v)
          } else if (typeof v === 'object' && v.line1) {
            ven = v
          } else {
            return undefined
          }
          let address = ven.line1
          address += ven.line2 ? ' ' + ven.line2 : ''
          address += ven.city ? ', ' + (isNaN(ven.city) ? ven.city : this.getCityById(ven.city).name) : ''
          address += '\n'
          address += ven.state ? ', ' + (isNaN(ven.state) ? ven.state : this.getStateById(ven.state).code) : ''
          address += ven.postal ? '  ' + ven.postal : ''
          return address
        }
      } else {
        return undefined
      }
    },
    defaultTaxcode () {
      return (this.selectedVendor && this.selectedVendor.default_tax_code) ? this.$store.getters.getTaxcodeById(this.selectedVendor.default_tax_code) : null
    },
    // Get only the first account
    defaultAccount () {
      return (this.selectedVendor && Array.isArray(this.selectedVendor.default_accounts) && this.selectedVendor.default_accounts.length) ? this.$store.getters.getAccountById(this.selectedVendor.default_accounts[0]) : null
    },
    // Get only the first Free Field
    defaultFreeField () {
      return (this.selectedVendor && Array.isArray(this.selectedVendor.default_free_fields) && this.selectedVendor.default_free_fields.length) ? this.$store.getters.getFreefieldById(this.selectedVendor.default_free_fields[0]) : null
    },
    // Get all Free Fields
    defaultFreeFields () {
      let results
      if (this.selectedVendor && Array.isArray(this.selectedVendor.default_free_fields) && this.selectedVendor.default_free_fields.length) {
        results = []
        for (const i in this.selectedVendor.default_free_fields) {
          const freeField = this.$store.getters.getFreefieldById(this.selectedVendor.default_free_fields[i])
          results.push(freeField)
        }
      }
      return results
    }
  },
  watch: {
    /**
     * Tracks whether the header or lines have been changed. If so, sets a store property that warns the user
     * that they will lose changes if they try to refresh the page.
     */
    hasBeenChanged () {
      this.$store.dispatch('isEditingForm', this.hasBeenChanged)
    }
  },
  created () {
    this.$store.dispatch('setHeaderForm', this.form)
    this.setSelectedPeriod()
    this.initialHeaderFormState = JSON.stringify(this.form)
  },
  mounted () {
    try {
      const coreLayout = this.$root.$children[0].$children[0]
      const { appHeader, navSide } = coreLayout.$refs
      const appHeaderHeight = appHeader.$el.offsetHeight
      const trxTitleContainerHeight = this.$refs.trxTitleContainer.$el.offsetHeight
      const trxHeaderContainerHeight = this.$refs.trxHeaderContainer.$el.offsetHeight
      const distLinesHeight = coreLayout.$el.offsetHeight - (appHeaderHeight + trxTitleContainerHeight + trxHeaderContainerHeight)
      if (navSide.$el.offsetWidth && trxTitleContainerHeight && this.$refs.mockTrxTitleContainer) {
        this.$refs.trxTitleContainer.$el.style.width = `calc(100% - ${navSide.$el.offsetWidth}px)`
        this.$refs.mockTrxTitleContainer.$el.style.height = `${trxTitleContainerHeight}px`
      }
      if (distLinesHeight) {
        this.distLinesHeight = distLinesHeight
      }
      this.focusOnFirstField()
    } catch (e) {
      console.log(e)
    }
  },
  /**
   * Pre-route enter logic. First, fetches settings. If BATCH_HANDLING setting exists and the destination route is one
   * in which the form contains a "batch" property, generates a user batch, otherwise fetches the last batch assigned to
   * the current user (normal behavior). Then, fetches the current period if it isn't already cached. Finally, checks if
   * the destination is NewPr, and if so, fetches the id of the source code 'PR'.
   * @param to
   * @param from
   * @param next
   * @return {Promise.<any>}
   */
  async beforeRouteEnter (to, from, next) {
    try {
      const { getCurrentPeriod, getUiSettings, getDefaultBatch, getBatchById, getUser } = store.getters
      const promises = []
      const settingsPromises = [
        store.dispatch('fetchUiSetting', { name: 'LINE_FIELD_ORDER' }),
        store.dispatch('fetchUiSetting', { name: 'BATCH_HANDLING' })
      ]
      await Promise.all(settingsPromises)
      const batchSetting = getUiSettings.BATCH_HANDLING === "''" ? null : getUiSettings.BATCH_HANDLING
      if (!batchSetting) {
        let batchPayload = {}
        if (to.query.batch && !isNaN(to.query.batch) && !getBatchById(parseInt(to.query.batch)).id) {
          batchPayload = { ...batchActionPayload, query: { id: to.query.batch } }
        } else {
          batchPayload = { ...batchActionPayload, query: { user__username: getUser.username, active: 'True' } }
        }
        const batches = await store.dispatch('fetchWithQuery', batchPayload)
        if (!(batches && batches.count) && !Object.keys(getDefaultBatch || {}).length) {
          promises.push(store.dispatch('fetchWithQuery', { ...batchActionPayload, query: { active: 'True' } }))
        }
      } else if (batchSetting && batchSetting !== 'BLANK' && !UserBatchGenerator.checkIfUserBatchIsCached()) { // If BATCH_HANDLING setting is defined and not 'BLANK', and the user batch isn't cached
        const todaysBatchExists = await UserBatchGenerator.checkIfUserBatchExists()
        if (!todaysBatchExists) { // If it isn't, well does it even exist (server-side)? No?
          const batchGenerator = new UserBatchGenerator([bankActionPayload, productionActionPayload, companyActionPayload])
          await batchGenerator.generateUserBatch() // Well then, create it!
        }
      }
      if (!(getCurrentPeriod && Object.keys(getCurrentPeriod).length)) {
        promises.push(store.dispatch('fetchPeriodsWithQuery', { active: 'True' }))
      }
      if (to.name === 'NewPr') {
        const { payload } = await import('@/components/RouteComponents/Sourcecodes/actionPayload')
        const sourcecodePayload = Object.assign({}, payload, { query: { code: 'PR' } })
        promises.push(store.dispatch('fetchWithQuery', sourcecodePayload))
      }
      await Promise.all(promises)
    } catch (e) {
      console.log(e)
    } finally {
      next()
    }
  },
  /**
   * Pre-route leave logic
   * @param to
   * @param from
   * @param next
   * @return {Promise.<any>}
   */
  beforeRouteLeave (to, from, next) {
    this.$store.dispatch('changeModalVisibility', { entityType: 'customerVendorForm', visible: false })
    this.$store.dispatch('changeModalVisibility', { entityType: 'batch', visible: false })
    if (this.hasBeenChanged && !this.changesWereSaved && !this.$store.getters.getRouteLeaveConfirmation) { // Show data loss warning if form or lines have been changed
      return this.$store.dispatch('setDestinationRoute', to)
        .then(() => this.$store.dispatch('changeModalVisibility', { entityType: 'changeLossModal', visible: true }))
        .catch(e => console.log(e))
    }
    this.altRoute = this.generateAltRoute(to)
    if (this.failNotif && this.failNotif.close && typeof this.failNotif.close === 'function') { // Close dist line fail notification
      this.failNotif.close()
    }
    if (this.isSaving && !this.exitWarningDialogShown) { // Show warning if user tries to leave while saving TODO: deprecated
      this.handleExitWarningDialogVisible(true)
    }
    if (!this.isSaving && this.hasBeenChanged && this.isSaveable) {
      this.handleDialogVisible(true)
    } else {
      this.$store.dispatch('isEditingForm', false)
      this.$store.dispatch('clearHeaderFormState')
        .then(() => next())
        .catch(e => {
          console.log(e)
          next()
        })
    }
    this.closeTrxAlreadyExistsMsg()
  }
}



// WEBPACK FOOTER //
// ./src/Mixins/TransactionCreateViews.js